import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtPrice, fmtPct, changeColor, signalClass } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Sparkles, Lock, Star, Radio, FileText, Zap } from "lucide-react";

const TFS = ["1D","1W","1M","1Y","5Y"];
const REALTIME_TFS = new Set(["1D","1W"]);

export default function CommodityDetail() {
  const { code } = useParams();
  const { user, refresh } = useAuth();
  const [meta, setMeta] = useState(null);
  const [quote, setQuote] = useState(null);
  const [tf, setTf] = useState("1M");
  const [series, setSeries] = useState([]);
  const [reco, setReco] = useState(null);
  const [recoLoading, setRecoLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [tfLocked, setTfLocked] = useState(false);
  const [eod, setEod] = useState(null);
  const [impact, setImpact] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [currencies, setCurrencies] = useState({ supported: ["USD"], all: ["USD"] });
  const [currency, setCurrency] = useState("USD");

  const isPro = user?.tier !== "free";
  const watched = (user?.watchlist || []).includes(code);

  const loadBase = async () => {
    const { data } = await api.get(`/commodities/${code}`);
    setMeta(data.meta); setQuote(data.quote);
    const [n, v, e, fx] = await Promise.all([
      api.get(`/analyst-notes?commodity=${code}`),
      api.get(`/vendors?commodity=${code}`),
      api.get(`/eod/${code}`),
      api.get(`/fx/currencies`),
    ]);
    setNotes(n.data); setVendors(v.data); setEod(e.data); setCurrencies(fx.data);
  };

  const reloadQuote = async (ccy) => {
    if (ccy === "USD") {
      const { data } = await api.get(`/commodities/${code}`);
      setQuote(data.quote);
    } else {
      const { data } = await api.get(`/commodities/quotes?currency=${ccy}`);
      const q = data.find(x => x.code === code);
      if (q) setQuote(q);
    }
  };

  const loadSeries = async (timeframe) => {
    try {
      const { data } = await api.get(`/commodities/${code}/history?timeframe=${timeframe}`);
      setSeries(data.series.map(p => ({...p, price: p.price, ts: new Date(p.t).toLocaleDateString()})));
      setTfLocked(false);
    } catch (e) {
      if (e.response?.status === 402) { setTfLocked(true); setSeries([]); }
    }
  };

  useEffect(() => { loadBase(); }, [code]);
  useEffect(() => { loadSeries(tf); }, [tf, code]);
  useEffect(() => { if (currency) reloadQuote(currency); /* eslint-disable-next-line */ }, [currency]);

  const runReco = async () => {
    if (!isPro) { toast.error("Upgrade to Pro for AI recommendations"); return; }
    setRecoLoading(true);
    try {
      const { data } = await api.get(`/ai/recommendation/${code}`);
      setReco(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI failed"); }
    finally { setRecoLoading(false); }
  };

  const runImpact = async () => {
    if (!isPro) { toast.error("Upgrade to Pro for cross-commodity impact"); return; }
    setImpactLoading(true);
    try {
      const { data } = await api.get(`/relationships/${code}`);
      setImpact(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setImpactLoading(false); }
  };

  const toggleWatch = async () => {
    const next = watched ? user.watchlist.filter(c => c !== code) : [...(user.watchlist||[]), code];
    await api.put("/watchlist", { codes: next });
    await refresh();
    toast.success(watched ? "Removed from watchlist" : "Added to watchlist");
  };

  if (!meta || !quote) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="commodity-detail">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link to="/app/markets" className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary">← Markets</Link>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">{meta.name}</h1>
          <div className="mt-1 flex items-center gap-3 text-xs font-mono uppercase tracking-widest text-muted-foreground">
            <span>{meta.exchange}</span><span>·</span><span>{meta.category}</span><span>·</span><span>{meta.unit}</span>
          </div>
        </div>
        <div className="flex items-end gap-6">
          <div className="text-right">
            <div className="flex items-center justify-end gap-2">
              {quote.data_grade === "realtime" ? (
                <Badge data-testid="feed-badge-realtime" variant="outline" className="signal-buy border uppercase text-[10px] tracking-widest"><Radio className="mr-1 h-3 w-3" />CME Direct</Badge>
              ) : (
                <Badge data-testid="feed-badge-delayed" variant="outline" className="uppercase text-[10px] tracking-widest text-muted-foreground">Yahoo · 15min delayed</Badge>
              )}
              {currencies.supported.length > 1 && (
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger data-testid="currency-select" className="h-7 w-20 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {currencies.supported.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="num text-4xl font-bold">{fmtPrice(quote.price)}</div>
            <div className={`num text-sm ${changeColor(quote.change_pct)}`}>{fmtPct(quote.change_pct)} · {quote.change > 0 ? "+" : ""}{fmtPrice(quote.change, 4)}</div>
          </div>
          <Button variant="outline" data-testid="detail-watch-toggle" onClick={toggleWatch} className="rounded-full">
            <Star className={`mr-2 h-4 w-4 ${watched ? "fill-primary text-primary" : ""}`} />
            {watched ? "Watching" : "Watch"}
          </Button>
        </div>
      </div>

      {/* CME session insights (Pro+ only) */}
      {quote.cme_insights && (
        <Card className="border-primary/30 bg-primary/[0.03]">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-mono uppercase tracking-widest text-primary flex items-center gap-2"><Radio className="h-4 w-4" />CME session insights</CardTitle>
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Real-time · {meta.exchange}</span>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
              {[
                ["Settlement", fmtPrice(quote.cme_insights.settlement)],
                ["Prev settle", fmtPrice(quote.cme_insights.prev_settlement)],
                ["Session open", fmtPrice(quote.cme_insights.session_open)],
                ["Session high", fmtPrice(quote.cme_insights.session_high)],
                ["Session low", fmtPrice(quote.cme_insights.session_low)],
                ["Tick size", quote.cme_insights.tick_size],
                ["Contract", quote.cme_insights.contract_size],
              ].map(([label, val]) => (
                <div key={label} data-testid={`cme-${label.toLowerCase().replace(/\s+/g,'-')}`}>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
                  <div className="num text-sm font-semibold">{val}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* chart */}
      <Card className="border-border/60 bg-card/60">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm font-mono uppercase tracking-widest text-muted-foreground">Price · {tf}</CardTitle>
          <div className="flex gap-1">
            {TFS.map(t => (
              <button
                key={t}
                data-testid={`tf-${t}`}
                onClick={() => setTf(t)}
                className={`rounded-md px-3 py-1 text-xs font-mono transition ${tf===t ? "bg-primary text-primary-foreground" : "border border-border/60 text-muted-foreground hover:text-foreground"}`}
              >
                {t}{!isPro && REALTIME_TFS.has(t) && <Lock className="ml-1 inline h-3 w-3" />}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {tfLocked ? (
            <div className="grid h-72 place-items-center rounded-md border border-dashed border-border/60 p-6 text-center">
              <div>
                <Lock className="mx-auto h-6 w-6 text-primary" />
                <div className="mt-3 font-display text-lg font-semibold">Real-time data locked</div>
                <p className="mt-1 text-sm text-muted-foreground">Upgrade to Pro for intraday and weekly charts.</p>
                <Link to="/app/pricing"><Button className="mt-4 rounded-full" size="sm">See plans</Button></Link>
              </div>
            </div>
          ) : (
            <div className="h-72" data-testid="price-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={series} margin={{top:10,right:10,left:0,bottom:0}}>
                  <defs>
                    <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35}/>
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" opacity={0.35}/>
                  <XAxis dataKey="ts" tick={{fontSize:10, fill:"hsl(var(--muted-foreground))"}} interval="preserveStartEnd" />
                  <YAxis domain={["auto","auto"]} tick={{fontSize:10, fill:"hsl(var(--muted-foreground))"}} width={55} />
                  <Tooltip contentStyle={{background:"hsl(var(--card))", border:"1px solid hsl(var(--border))", fontSize:12}} />
                  <Area type="monotone" dataKey="price" stroke="hsl(var(--primary))" strokeWidth={1.8} fill="url(#grad)"/>
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* EOD daily recap */}
      {eod && (
        <Card data-testid="eod-recap" className="border-border/60 bg-card/60">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <FileText className="h-4 w-4" />EOD Recap · {eod.date}
              <span className={`ml-2 text-[10px] ${eod.sentiment==="bullish"?"text-[hsl(var(--signal-buy))]":eod.sentiment==="bearish"?"text-[hsl(var(--signal-wait))]":"text-[hsl(var(--signal-hold))]"}`}>
                · {eod.sentiment}
              </span>
            </CardTitle>
            {eod.locked && <Badge variant="outline" className="uppercase text-[10px]"><Lock className="mr-1 h-3 w-3" />Pro</Badge>}
          </CardHeader>
          <CardContent>
            <div className="font-display text-lg font-semibold">{eod.headline}</div>
            <p className="mt-2 text-sm leading-relaxed text-foreground">{eod.one_liner}</p>
            {eod.locked ? (
              <div className="mt-4 rounded-md border border-primary/40 bg-primary/5 p-3 text-xs text-muted-foreground">
                Full liquidity readout, trend structure and close read available on Pro.
                <Link to="/app/pricing" className="ml-1 font-semibold text-primary hover:underline">Upgrade →</Link>
              </div>
            ) : (
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <div><div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Liquidity</div><p className="mt-1 text-xs leading-relaxed">{eod.liquidity}</p></div>
                <div><div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">How it traded</div><p className="mt-1 text-xs leading-relaxed">{eod.trend}</p></div>
                <div><div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Close read</div><p className="mt-1 text-xs leading-relaxed">{eod.close_read}</p></div>
              </div>
            )}
            <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Written by {eod.model_used || "claude-sonnet-5"} · Market insight, not financial advice</div>
          </CardContent>
        </Card>
      )}

      {/* AI reco */}
      <Card className="border-border/60 bg-card/60">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm font-mono uppercase tracking-widest text-muted-foreground">AI recommendation</CardTitle>
          <Button size="sm" variant="outline" data-testid="run-reco-btn" onClick={runReco} disabled={recoLoading} className="rounded-full">
            <Sparkles className="mr-2 h-4 w-4" /> {recoLoading ? "Analysing…" : reco ? "Refresh" : "Generate"}
          </Button>
        </CardHeader>
        <CardContent>
          {!isPro && (
            <div className="flex items-center gap-3 rounded-md border border-primary/40 bg-primary/5 p-4">
              <Lock className="h-4 w-4 text-primary" />
              <div className="flex-1">
                <div className="font-display text-sm font-semibold">AI signals are a Pro feature</div>
                <p className="text-xs text-muted-foreground">Powered by Claude Sonnet 5. Includes structured Buy/Hold/Wait verdict with rationale and risks.</p>
              </div>
              <Link to="/app/pricing"><Button size="sm" className="rounded-full">Upgrade</Button></Link>
            </div>
          )}
          {isPro && !reco && !recoLoading && (
            <div className="text-sm text-muted-foreground">Click Generate to produce a fresh Buy/Hold/Wait verdict for {meta.name}.</div>
          )}
          {reco && (
            <div className="grid gap-4 lg:grid-cols-[auto_1fr]">
              <div className={`rounded-lg border p-5 ${signalClass(reco.signal)}`}>
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-80">Verdict</div>
                <div className="mt-1 font-display text-4xl font-bold">{reco.signal}</div>
                <div className="mt-2 text-xs font-mono uppercase tracking-wider opacity-80">Confidence {reco.confidence}%</div>
                <div className="mt-4 text-xs">
                  <div className="opacity-80">Target entry</div>
                  <div className="num text-base font-semibold">{fmtPrice(reco.target_entry_low)} – {fmtPrice(reco.target_entry_high)}</div>
                </div>
                <div className="mt-3 text-xs">
                  <div className="opacity-80">Horizon</div>
                  <div className="num text-base font-semibold">{reco.horizon_days} days</div>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Rationale</div>
                  <ul className="mt-2 space-y-1.5 text-sm text-foreground">
                    {(reco.rationale || []).map((r, i) => <li key={i} className="flex gap-2"><span className="text-primary">▸</span>{r}</li>)}
                  </ul>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Key risks</div>
                  <ul className="mt-2 space-y-1.5 text-sm text-foreground">
                    {(reco.risks || []).map((r, i) => <li key={i} className="flex gap-2"><span className="text-[hsl(var(--signal-wait))]">▸</span>{r}</li>)}
                  </ul>
                </div>
                <div className="rounded-md border border-border/60 bg-background/50 p-3 text-[11px] text-muted-foreground">
                  <span className="font-mono uppercase tracking-widest text-primary">Model:</span> {reco.model_used} · <span className="italic">{reco.disclaimer}</span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cross-commodity impact */}
      <Card className="border-border/60 bg-card/60">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2"><Radio className="h-4 w-4" />Cross-commodity impact</CardTitle>
          <Button size="sm" variant="outline" data-testid="run-impact-btn" onClick={runImpact} disabled={impactLoading} className="rounded-full">
            <Zap className="mr-2 h-4 w-4" /> {impactLoading ? "Analysing…" : impact ? "Refresh" : "Generate"}
          </Button>
        </CardHeader>
        <CardContent>
          {!isPro && (
            <div className="flex items-center gap-3 rounded-md border border-primary/40 bg-primary/5 p-4">
              <Lock className="h-4 w-4 text-primary" />
              <div className="flex-1">
                <div className="font-display text-sm font-semibold">Cascade analysis is a Pro feature</div>
                <p className="text-xs text-muted-foreground">See how {meta.name} price moves ripple across feed, dairy, packaging and downstream products with short/medium/long-term commentary.</p>
              </div>
              <Link to="/app/pricing"><Button size="sm" className="rounded-full">Upgrade</Button></Link>
            </div>
          )}
          {isPro && !impact && !impactLoading && (
            <div className="text-sm text-muted-foreground">Click Generate to see how {meta.name} cascades through feed, dairy, packaging and finished-goods costs.</div>
          )}
          {impact && (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-md border border-border/60 bg-background/50 p-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-primary">Short term · 0-30d</div>
                  <p className="mt-2 text-xs leading-relaxed">{impact.short_term}</p>
                </div>
                <div className="rounded-md border border-border/60 bg-background/50 p-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-primary">Medium term · 30-90d</div>
                  <p className="mt-2 text-xs leading-relaxed">{impact.medium_term}</p>
                </div>
                <div className="rounded-md border border-border/60 bg-background/50 p-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-primary">Long term · 90-365d</div>
                  <p className="mt-2 text-xs leading-relaxed">{impact.long_term}</p>
                </div>
              </div>
              {impact.supply_disruption && (
                <div className="rounded-md border border-[hsl(var(--signal-wait))]/40 bg-[hsl(var(--signal-wait))]/5 p-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[hsl(var(--signal-wait))]">Supply disruption scenario</div>
                  <p className="mt-2 text-xs leading-relaxed">{impact.supply_disruption}</p>
                </div>
              )}
              {impact.cascades?.length > 0 && (
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Cascades</div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {impact.cascades.map((c, i) => (
                      <div key={i} data-testid={`cascade-${i}`} className="rounded-md border border-border/60 bg-background/50 p-3">
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-[10px] uppercase tracking-widest">
                            <Link to={`/commodity/${c.from}`} className="text-primary hover:underline">{c.from}</Link>
                            <span className="mx-1 text-muted-foreground">→</span>
                            <Link to={`/commodity/${c.to}`} className="text-primary hover:underline">{c.to}</Link>
                          </span>
                          <Badge variant="outline" className={`text-[10px] ${c.magnitude==="large"?"signal-wait border":c.magnitude==="moderate"?"signal-hold border":"signal-buy border"}`}>{c.magnitude} · {c.horizon}</Badge>
                        </div>
                        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{c.takeaway}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="rounded-md border border-border/60 bg-background/50 p-3 text-[11px] text-muted-foreground">
                <span className="font-mono uppercase tracking-widest text-primary">Model:</span> {impact.model_used} · <span className="italic">{impact.disclaimer}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* notes + vendors */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border-border/60 bg-card/60">
          <CardHeader><CardTitle className="text-sm font-mono uppercase tracking-widest text-muted-foreground">Analyst notes</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {notes.length === 0 && <div className="text-sm text-muted-foreground">No notes yet.</div>}
            {notes.map(n => (
              <div key={n.id} data-testid={`detail-note-${n.id}`} className="rounded-md border border-border/60 bg-background/50 p-3">
                <div className="flex items-center justify-between">
                  <div className="font-display text-sm font-semibold">{n.title}</div>
                  {n.locked && <Badge variant="outline" className="uppercase text-[10px]"><Lock className="mr-1 h-3 w-3" />{n.tier_required}</Badge>}
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{n.author}</div>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{n.body}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-border/60 bg-card/60">
          <CardHeader><CardTitle className="text-sm font-mono uppercase tracking-widest text-muted-foreground">Vendor comparison</CardTitle></CardHeader>
          <CardContent>
            <div className="term-scroll overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                  <tr className="border-b border-border/60"><th className="py-2 text-left">Vendor</th><th className="py-2 text-right">Basis</th><th className="py-2 text-right">Lead</th><th className="py-2 text-center">Risk</th></tr>
                </thead>
                <tbody className="divide-y divide-border/40">
                  {vendors.map(v => (
                    <tr key={v.id} data-testid={`detail-vendor-${v.id}`}>
                      <td className="py-2">
                        <div className="font-semibold">{v.name}</div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{v.country}</div>
                      </td>
                      <td className="py-2 text-right num">
                        {v.locked ? <Lock className="ml-auto h-3 w-3 text-muted-foreground" /> : (v.basis_price >= 0 ? "+" : "") + v.basis_price?.toFixed(2)}
                      </td>
                      <td className="py-2 text-right num">{v.lead_time_days}d</td>
                      <td className="py-2 text-center"><Badge variant="outline" className="text-[10px]">{v.risk_rating}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Link to="/app/vendors" className="mt-3 block text-xs text-primary hover:underline">Full vendor comparison →</Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
